import { useLayoutEffect, useMemo, useState } from 'react';
import {
  BestiaryCardData,
  BestiaryLootDisplay,
} from '../../utils/bestiary.utils';
import { BestiaryCard } from './BestiaryCard';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { ItemCard } from './ItemCard';
import { KeyDisplay } from './KeyDisplay';

interface Props {
  card: BestiaryCardData;
  onClose: () => void;
}

function lootSubtitle(l: BestiaryLootDisplay): string {
  const pct = Math.round(l.chance * 100);
  const qty =
    l.quantityMax > 1 || l.quantityMin > 1
      ? ` · x${l.quantityMin}${l.quantityMax > l.quantityMin ? `-${l.quantityMax}` : ''}`
      : '';
  const elite = l.eliteOnly ? ' (élite)' : '';
  return `Taux de drop : ${pct} %${qty}${elite}`;
}

export const BestiaryCardLightbox = ({ card, onClose }: Props) => {
  const nav = useMenuNavigation({ onConfirm: onClose, onCancel: onClose });
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(
    null,
  );

  const focusedLoot = useMemo<BestiaryLootDisplay | null>(() => {
    if (!nav.focusedId?.startsWith('loot:')) {
      return null;
    }
    const idx = Number.parseInt(nav.focusedId.slice('loot:'.length), 10);
    return card.loot[idx] ?? null;
  }, [nav.focusedId, card.loot]);

  // Position the floating ItemCard just below the focused loot cell, in the
  // overlay's coordinate space (the overlay is `position: fixed`).
  useLayoutEffect(() => {
    const root = nav.containerRef.current;
    if (!nav.focusedId || !root) {
      setCardPos(null);
      return;
    }
    const cell = root.querySelector<HTMLElement>(
      `[data-nav-id="${nav.focusedId}"]`,
    );
    if (!cell) {
      setCardPos(null);
      return;
    }
    let top = 0;
    let left = 0;
    let cur: HTMLElement | null = cell;
    while (cur && cur !== root) {
      top += cur.offsetTop;
      left += cur.offsetLeft;
      cur = cur.offsetParent as HTMLElement | null;
    }
    setCardPos({
      top: top + cell.offsetHeight + 12,
      left: left + cell.offsetWidth / 2,
    });
  }, [nav.focusedId, nav.containerRef]);

  return (
    <div className="bestiary-lightbox-overlay" ref={nav.containerRef}>
      <BestiaryCard card={card} large />
      {focusedLoot && cardPos && (
        <ItemCard
          icon={focusedLoot.sprite}
          overlay={focusedLoot.overlay}
          name={focusedLoot.label}
          subtitle={lootSubtitle(focusedLoot)}
          pos={cardPos}
        />
      )}
      <div className="bestiary-lightbox-hint">
        <KeyDisplay context="interface" action="CANCEL" size="sm" /> fermer
      </div>
    </div>
  );
};
